/**
 * TemporaryRackCell Component
 * Always-visible inline editable cell for temporary rack values in order tables
 * Used for single glazed leaded orders instead of regular rack
 * Shows input box with Save and Clear buttons at all times
 */

import React, { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { useUpdateOrderMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import type { OrderSummary } from '../../utils/types';
import { RACK_FIELD_MAX_LENGTH } from '../../utils/rackConstants';

interface TemporaryRackCellProps {
  order: OrderSummary;
  className?: string;
  readOnly?: boolean;
}

export const TemporaryRackCell: React.FC<TemporaryRackCellProps> = ({ order, className = '', readOnly = false }) => {
  const [editValue, setEditValue] = useState(order.temporary_rack || '');
  const [updateOrder, { isLoading }] = useUpdateOrderMutation();
  const toast = useToast();

  // Sync editValue with order.temporary_rack when order updates
  useEffect(() => {
    setEditValue(order.temporary_rack || '');
  }, [order.temporary_rack]);

  const handleSave = async () => {
    try {
      const newValue = editValue.trim() || null;
      const response = await updateOrder({ 
        id: order.id, 
        data: { temporary_rack: newValue }
      }).unwrap();
      
      // Update local state with the response to ensure sync
      setEditValue(response.temporary_rack || '');
      
      toast.success('Temporary Rack updated', `Temporary Rack ${newValue ? `set to "${newValue}"` : 'cleared'}`);
    } catch (e: any) {
      toast.error('Update failed', e?.data?.detail || 'Please try again.');
    }
  };

  const handleClear = async () => {
    try {
      const response = await updateOrder({ 
        id: order.id, 
        data: { temporary_rack: null }
      }).unwrap();
      
      // Update local state with the response to ensure sync
      setEditValue(response.temporary_rack || '');
      
      toast.success('Temporary Rack cleared', 'Temporary Rack value has been removed');
    } catch (e: any) {
      toast.error('Update failed', e?.data?.detail || 'Please try again.');
      // Restore previous value on error
      setEditValue(order.temporary_rack || '');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  const cellClasses = [
    'p-2 border',
    'text-left',
    className
  ].filter(Boolean).join(' ');

  // Read-only mode: just display the value (or '-' if empty)
  if (readOnly) {
    return (
      <td className={cellClasses}>
        <span className="text-sm text-gray-900">
          {order.temporary_rack || '-'}
        </span>
      </td>
    );
  }

  return (
    <td className={cellClasses}>
      <div className="flex flex-col gap-2 w-full max-w-[280px]">
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-2 border border-yellow-400 bg-yellow-50 rounded text-sm focus:outline-none focus:border-yellow-600"
          placeholder="T-A1234"
          maxLength={RACK_FIELD_MAX_LENGTH}
          disabled={isLoading}
        />
        <div className="flex gap-1">
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-semibold rounded disabled:opacity-50 transition-colors"
            title="Save temporary rack value"
          >
            SAVE
          </button>
          <button
            onClick={handleClear}
            disabled={isLoading}
            className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50 transition-colors"
            title="Clear temporary rack value"
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </td>
  );
};

TemporaryRackCell.displayName = 'TemporaryRackCell';
